/**
 * Data path for the Wiseguy Alerts widget.
 *
 * Session mode: send `date=` (ET today, or a caller-pinned day) so this
 * widget owns the session. Empty page before the first print is a quiet
 * morning, not "unavailable". A date picker later passes `date` instead of
 * defaulting to today. Search mode: ticker-scoped wide lookback.
 * Live (`tradealert:wsj`): same product-floor membership as REST (QF 2026-08);
 * user filters only hide rows for display — do not re-apply membership gates.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  adaptFznAlert,
  fznService,
  isFznUnpromote,
  normalizeFznLivePayload,
} from '../services/fznService';
import { onQfConnected, qfSubscribe } from '../services/qfClient';
import {
  alertKey,
  alertsFromStore,
  etCalendarDate,
  etDayOf,
  etToday,
  isOnEtDay,
  removeAlert,
  replaceFromHistory,
  upsertAlert,
} from '../components/wiseguy/alertStore';
import type { WiseguyAlert } from '../types/wiseguy';

/** Live QF channel — product-floored Steamroom feed (matches REST membership). */
export const WISEGUY_LIVE_CHANNEL = 'tradealert:wsj';

/**
 * History page size. QF's server cap is 5000.
 *
 * Sized off measured floored volume, not the unfloored Django mirror: QF's
 * `/v1/steamroom/alerts/history` returned 446 rows for 2026-08-19 and 680 for
 * 2026-08-04, the busiest session in the trailing 15. The floor passes roughly
 * 40-48% of the raw `source='uw'` count, so ~700 is the realistic high-water
 * mark and 1000 leaves headroom.
 *
 * This is a hard cap, not a page: the widget has no "load more" control since
 * the FZN cutover, so anything past the limit is unreachable — the user only
 * sees the `truncated` banner. Do not lower it without restoring paging.
 */
export const SESSION_LIMIT = 1000;

/** Ticker search lookback in calendar days (inclusive window). */
export const SEARCH_LOOKBACK_DAYS = 365;

/** Ticker search row cap. */
export const SEARCH_LIMIT = 2000;

export type WiseguyAlertsMode = 'session' | 'search';

export interface UseWiseguyAlertsOptions {
  /**
   * Symbol search string (quick filter and/or preset symbol).
   * Comma-separated supported; first symbol drives the ticker REST path when
   * exactly one is set. Multiple symbols stay in session mode and filter client-side.
   */
  symbolFilter: string;
  /** Extra instance key so multiple dashboard tiles do not share in-memory store. */
  instanceId?: string;
  /**
   * Pin session history to this ET day (`YYYY-MM-DD`). Default: ET today.
   * Wire a date picker by passing the selected day; omit for the live tape.
   */
  date?: string;
}

export interface UseWiseguyAlertsResult {
  mode: WiseguyAlertsMode;
  /** Full store, newest-first (not yet user-filter-applied beyond mode rules). */
  alerts: WiseguyAlert[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  truncated: boolean;
  /**
   * Session mode: the day we requested (ET today, or the pinned `date`).
   * Search mode: ticker + lookback label.
   */
  scopeLabel: string;
  /** Session ET day in session mode; empty in search / before load. */
  sessionDate: string;
  refetch: () => void;
  /**
   * Keys that arrived via alert.update this session (for flash/UI).
   * Cleared by the consumer if desired.
   */
  recentlyUpdatedKeys: ReadonlySet<string>;
  clearRecentlyUpdated: () => void;
  /** True when the latest live event was alert.new for this key (flash). */
  recentlyNewKeys: ReadonlySet<string>;
  clearRecentlyNew: () => void;
}

function parseSymbols(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter((s) => s.length > 0);
}

function resolveMode(symbolFilter: string): {
  mode: WiseguyAlertsMode;
  searchSymbol: string | null;
} {
  const symbols = parseSymbols(symbolFilter);
  // Single ticker → server search with wide lookback (HTZ case).
  // Zero or multi → session tape; multi filtered client-side.
  if (symbols.length === 1) {
    return { mode: 'search', searchSymbol: symbols[0] };
  }
  return { mode: 'session', searchSymbol: null };
}

async function fetchSessionHistory(date: string): Promise<{
  alerts: WiseguyAlert[];
  truncated: boolean;
  sessionDate: string;
}> {
  // Explicit date= so this widget (not QF omit-date) picks the day.
  // Default is ET today; a date picker later passes a different day.
  const res = await fznService.getAlerts({
    limit: SESSION_LIMIT,
    date,
  });
  return {
    alerts: res.data,
    truncated: res.truncated,
    sessionDate: res.session_date || date,
  };
}

async function fetchTickerSearch(symbol: string): Promise<{
  alerts: WiseguyAlert[];
  truncated: boolean;
}> {
  const to = etToday();
  const from = etCalendarDate(SEARCH_LOOKBACK_DAYS - 1);
  const res = await fznService.getTickerAlerts(symbol, {
    from,
    to,
    limit: SEARCH_LIMIT,
  });
  return { alerts: res.data, truncated: res.truncated };
}

/** Browser abort / 499 / dropped connection — not a board-auth failure. */
export function isTransientFetchFailure(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  if (err.name === 'AbortError') return true;
  const msg = err.message.toLowerCase();
  return (
    msg === 'failed to fetch' ||
    msg.includes('networkerror') ||
    msg.includes('abort')
  );
}

/**
 * Server said "busy, try again" — QF returns 503 + Retry-After when its
 * 8-slot alert query pool is saturated, which is exactly what the open does.
 *
 * Kept separate from `isTransientFetchFailure`: that predicate also decides
 * which errors are swallowed without surfacing, and a pool 503 that outlives
 * its retry SHOULD reach the user rather than leave a silently stale tape.
 */
export function isRetryableServerFailure(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return /->\s*(429|502|503|504)\b/.test(err.message);
}

/** Skip a reconnect refetch if a history call just landed or is still out. */
const RECONNECT_COALESCE_MS = 8_000;

/**
 * Upper bound for the day-flip refetch jitter.
 *
 * Every member's board flips on the same first-of-day print, so an undelayed
 * refetch is a synchronized stampede against QF's 8-slot alert query pool.
 * Spreading arrivals over this window keeps the pool under its limit; the
 * flipping row is already painted (pinned into preferLiveKeys), so the delay
 * costs no visible latency.
 */
const FLIP_REFETCH_MAX_JITTER_MS = 3_000;

/** Random delay in [0, FLIP_REFETCH_MAX_JITTER_MS) for the day-flip refetch. */
export function flipRefetchDelayMs(
  random: () => number = Math.random
): number {
  return Math.floor(random() * FLIP_REFETCH_MAX_JITTER_MS);
}

/**
 * Upper bound for a calendar-driven refetch (overnight tab, tab becoming
 * visible). Wider than the live-print jitter: these clients are not waiting
 * on a painted row, and spreading them across half a minute is the whole
 * point of flipping before the open.
 */
const CALENDAR_FLIP_MAX_JITTER_MS = 30_000;

/** How often an overnight tab checks whether the ET calendar day has moved. */
const CALENDAR_POLL_MS = 60_000;

/** Random delay in [0, CALENDAR_FLIP_MAX_JITTER_MS) for a calendar refetch. */
export function calendarFlipDelayMs(
  random: () => number = Math.random
): number {
  return Math.floor(random() * CALENDAR_FLIP_MAX_JITTER_MS);
}

export function useWiseguyAlerts(
  options: UseWiseguyAlertsOptions
): UseWiseguyAlertsResult {
  const { symbolFilter, date: pinnedDate } = options;
  const { mode, searchSymbol } = useMemo(
    () => resolveMode(symbolFilter),
    [symbolFilter]
  );

  const [store, setStore] = useState<Map<string, WiseguyAlert>>(() => new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [recentlyUpdatedKeys, setRecentlyUpdatedKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [recentlyNewKeys, setRecentlyNewKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [sessionDate, setSessionDate] = useState('');

  // Live update keys that should beat the next history replace for overlay.
  const preferLiveKeysRef = useRef<Set<string>>(new Set());
  // Pending jittered day-flip refetch, cleared on unmount so a board closed
  // during the open does not still fire a history GET seconds later.
  const flipTimerRef = useRef<number | undefined>(undefined);
  const fetchGenRef = useRef(0);
  const inflightRef = useRef(0);
  const lastOkAtRef = useRef(0);
  const modeRef = useRef(mode);
  const searchSymbolRef = useRef(searchSymbol);
  const sessionDateRef = useRef('');
  const pinnedDateRef = useRef<string | undefined>(pinnedDate);
  modeRef.current = mode;
  searchSymbolRef.current = searchSymbol;
  sessionDateRef.current = sessionDate;
  pinnedDateRef.current = pinnedDate;

  const loadHistory = useCallback(async (reason: 'mount' | 'refetch' | 'mode') => {
    const gen = ++fetchGenRef.current;
    const currentMode = modeRef.current;
    const symbol = searchSymbolRef.current;
    inflightRef.current += 1;

    if (reason === 'mount' || reason === 'mode') {
      setIsLoading(true);
    } else {
      setIsFetching(true);
    }
    setError(null);

    const pull = async (): Promise<{
      alerts: WiseguyAlert[];
      truncated: boolean;
      sessionDate?: string;
    }> =>
      currentMode === 'search' && symbol
        ? await fetchTickerSearch(symbol)
        : await fetchSessionHistory(pinnedDateRef.current || etToday());

    try {
      let result: Awaited<ReturnType<typeof pull>>;
      try {
        result = await pull();
      } catch (err) {
        // One retry on a dropped first paint (Traefik 499). Reconnect
        // refetches are already coalesced and should not stack.
        //
        // A pool 503 is retried for EVERY reason, day-flip refetches
        // included: at the open the first attempt is the one most likely to
        // land in the burst, and giving up there is what surfaces as the
        // widget "failing to fetch" at 09:30.
        const poolBusy = isRetryableServerFailure(err);
        const retryable = poolBusy
          ? true
          : isTransientFetchFailure(err) && reason !== 'refetch';
        if (!retryable) throw err;
        // Back off before re-entering a pool that just rejected us, or the
        // retry is simply more of the load that caused the 503.
        if (poolBusy) {
          await new Promise((r) =>
            window.setTimeout(r, flipRefetchDelayMs())
          );
        }
        result = await pull();
      }

      if (gen !== fetchGenRef.current) return;

      setTruncated(result.truncated);
      if (currentMode === 'session') {
        // Do not walk the caption backwards. A live flip can set today's
        // date before omit-date history has flipped; overwriting with
        // yesterday would re-hide the new row and retrigger the flip.
        const incoming =
          'sessionDate' in result && typeof result.sessionDate === 'string'
            ? result.sessionDate
            : '';
        const current = sessionDateRef.current;
        if (!current || incoming >= current) {
          sessionDateRef.current = incoming;
          setSessionDate(incoming);
        }
      }
      setStore((prev) =>
        replaceFromHistory(prev, result.alerts, preferLiveKeysRef.current)
      );
      lastOkAtRef.current = Date.now();
    } catch (err) {
      if (gen !== fetchGenRef.current) return;
      // A 499 / tab-hide abort is not "auth died". Keep the tape we have.
      if (isTransientFetchFailure(err)) return;
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      inflightRef.current = Math.max(0, inflightRef.current - 1);
      if (gen === fetchGenRef.current) {
        setIsLoading(false);
        setIsFetching(false);
      }
    }
  }, []);

  // Fetch when mode / search symbol changes.
  useEffect(() => {
    // Mode switch: clear prefer-live so we don't retain unrelated session keys.
    preferLiveKeysRef.current = new Set();
    setStore(new Map());
    sessionDateRef.current = '';
    setSessionDate('');
    void loadHistory('mode');
  }, [mode, searchSymbol, pinnedDate, loadHistory]);

  // Live subscription — always on; mode only affects which rows we keep.
  useEffect(() => {
    const unsub = qfSubscribe(WISEGUY_LIVE_CHANNEL, (raw: unknown) => {
      if (!raw || typeof raw !== 'object') return;
      const payload = raw as Record<string, unknown>;
      if (!payload.external_id) return;

      const isUpdate = payload.type === 'alert.update';
      const row = normalizeFznLivePayload(payload);
      const alert = adaptFznAlert(row);
      const key = alertKey(alert);
      if (!key || key === '#0') return;

      const currentMode = modeRef.current;
      const symbol = searchSymbolRef.current;

      // Session: keep rows for the board's day. A live row on a newer ET day
      // is the overnight-tab flip — adopt that day and refetch `date=today`
      // so Friday does not stay mixed in. A pinned date (picker) does not
      // follow the live tape. Process alert.update even off-session so a
      // de-promote can drop the row.
      if (currentMode === 'session' && !isUpdate) {
        const pinned = pinnedDateRef.current;
        if (pinned) {
          if (!isOnEtDay(alert, pinned)) return;
        }
        const boardDay = sessionDateRef.current;
        const alertDay = etDayOf(alert.received_on);
        if (!pinned && boardDay && alertDay && alertDay > boardDay) {
          // Pin BEFORE the refetch. QF writes the alert to ClickHouse ~2s
          // after it reaches Centrifugo, so the history page this flip is
          // about to request cannot contain this row yet. Unpinned, the row
          // painted below is erased by its own refetch (AVGO 09:30:11 ET,
          // c8fd40ef8dbed8be). preferLiveKeys is released once history
          // carries the row.
          //
          // Fall through after scheduling: the shared tail below paints the
          // row and runs the new-row flash, so a flipping alert behaves like
          // any other new print.
          preferLiveKeysRef.current.add(key);

          sessionDateRef.current = alertDay;
          setSessionDate(alertDay);

          // sessionDateRef advances synchronously above, so later alerts in
          // this burst no longer see a stale boardDay and cannot re-enter
          // this branch — one flip per client, per day.
          //
          // The load problem is across clients: every connected member flips
          // on the SAME first-of-day print, so an undelayed refetch is a
          // synchronized stampede against QF's 8-slot alert query pool
          // (measured 2026-08-27: 16 req/s and 95 x 503 at 13:30 ET).
          // Jitter spreads the herd; the row is already painted by the pin
          // above, so the delay costs no visible latency.
          window.clearTimeout(flipTimerRef.current);
          flipTimerRef.current = window.setTimeout(() => {
            flipTimerRef.current = undefined;
            void loadHistory('refetch');
          }, flipRefetchDelayMs());
        } else if (boardDay && !isOnEtDay(alert, boardDay)) {
          return;
        }
      }

      // Search: only ingest rows for the searched symbol (user filter is the
      // search itself). Multi-symbol client filter is handled in the widget.
      if (currentMode === 'search' && symbol) {
        if (alert.symbol.toUpperCase() !== symbol) return;
      }

      // QF mirrors de-promotes onto tradealert:wsj. Overlay all-false +
      // below-floor means the row left membership — delete, and do not pin
      // it through the next history replace via preferLiveKeys.
      if (isUpdate && isFznUnpromote(row)) {
        preferLiveKeysRef.current.delete(key);
        setStore((prev) => removeAlert(prev, key));
        return;
      }

      if (isUpdate) {
        preferLiveKeysRef.current.add(key);
        setRecentlyUpdatedKeys((prev) => new Set(prev).add(key));
        setTimeout(() => {
          setRecentlyUpdatedKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }, 1000);
      } else {
        setRecentlyNewKeys((prev) => new Set(prev).add(key));
        setTimeout(() => {
          setRecentlyNewKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }, 1000);
      }

      setStore((prev) => upsertAlert(prev, alert));
    });

    // Mount already pulled history. The initial `connected` (and a reconnect
    // that lands within a few seconds) used to fire a second history GET;
    // the loser 499'd and painted "Error loading alerts".
    const unwatch = onQfConnected(() => {
      if (
        inflightRef.current > 0 ||
        Date.now() - lastOkAtRef.current < RECONNECT_COALESCE_MS
      ) {
        return;
      }
      void loadHistory('refetch');
    });

    // Overnight tab / laptop lid: do not wait for the first live print of
    // the new ET day. Refetch with date=etToday(); QF echoes that day, empty
    // if nothing has printed yet. A pinned picker date does not auto-advance.
    const scheduleCalendarRefetch = () => {
      if (modeRef.current !== 'session') return;
      if (pinnedDateRef.current) return;
      const board = sessionDateRef.current;
      if (!board || etToday() <= board) return;
      if (inflightRef.current > 0) return;
      if (flipTimerRef.current !== undefined) return;
      flipTimerRef.current = window.setTimeout(() => {
        flipTimerRef.current = undefined;
        void loadHistory('refetch');
      }, calendarFlipDelayMs());
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') scheduleCalendarRefetch();
    };
    document.addEventListener('visibilitychange', onVis);
    const calendarPoll = window.setInterval(
      scheduleCalendarRefetch,
      CALENDAR_POLL_MS
    );

    return () => {
      unsub();
      unwatch();
      window.clearTimeout(flipTimerRef.current);
      flipTimerRef.current = undefined;
      window.clearInterval(calendarPoll);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadHistory]);

  const alerts = useMemo(() => {
    const all = alertsFromStore(store);
    // One session on the tape. A live flip updates sessionDate before the
    // refetch lands; without this filter yesterday's rows would sit next to
    // the first print of the new day.
    if (mode === 'session' && sessionDate) {
      return all.filter((a) => isOnEtDay(a, sessionDate));
    }
    return all;
  }, [store, mode, sessionDate]);

  const scopeLabel = useMemo(() => {
    if (mode === 'search' && searchSymbol) {
      return `${searchSymbol} · last ${SEARCH_LOOKBACK_DAYS}d`;
    }
    return sessionDate;
  }, [mode, searchSymbol, sessionDate]);

  const clearRecentlyUpdated = useCallback(() => {
    setRecentlyUpdatedKeys(new Set());
  }, []);

  const clearRecentlyNew = useCallback(() => {
    setRecentlyNewKeys(new Set());
  }, []);

  const refetch = useCallback(() => {
    void loadHistory('refetch');
  }, [loadHistory]);

  return {
    mode,
    alerts,
    isLoading,
    isFetching,
    error,
    truncated,
    scopeLabel,
    sessionDate,
    refetch,
    recentlyUpdatedKeys,
    clearRecentlyUpdated,
    recentlyNewKeys,
    clearRecentlyNew,
  };
}
